import {
  useCheckLogin,
  useDeletePatient,
  useGetActivePatients,
  useGetAllPatients,
  useUpdatePatient,
} from '_api';
import { Patient, PatientPayload } from '_api/types';
import { useMemo } from 'react';

const usePatients = ({
  page = 0,
  size = 1000,
  withAlerts = true,
}: { page?: number; size?: number; withAlerts?: boolean } = {}) => {
  const { data: account } = useCheckLogin();
  const { mutateAsync: deletePatient } = useDeletePatient();
  const { mutateAsync: updatePatient } = useUpdatePatient();

  const { data: activePatients, isFetching: isActiveFetching } = useGetActivePatients({
    page,
    size,
    withAlerts,
  });

  const { data: allPatients, isFetching: isLoading } = useGetAllPatients({
    page,
    size,
  });

  const therapistPatients = useMemo(
    () =>
      activePatients?.filter((patient) =>
        patient.therapists.some(({ id }) => id === account?.id),
      ),
    [activePatients],
  );

  const therapistPatientsOptions = useMemo(
    () =>
      therapistPatients
        ? therapistPatients.map((patient) => ({
            description: patient.firstName + ' ' + patient.lastName,
            name: patient.id,
          }))
        : [],
    [therapistPatients],
  );

  const searchPatients = useMemo(
    () =>
      activePatients?.filter(
        (patient) => !patient.therapists.some(({ id }) => id === account?.id),
      ),
    [activePatients],
  );

  const therapistHospitalPatientMRNs = useMemo(
    () =>
      activePatients
        ?.map((patient) => patient.mrn)
        .filter((value) => value !== undefined && value !== '') as string[],
    [activePatients],
  );

  const getPatientMRNsByHospital = (hospitalId: number) => {
    const hospitalPatients = activePatients?.filter((patient) =>
      patient.therapists.some(({ hospital }) => hospital.id === hospitalId),
    );
    return hospitalPatients
      ?.map((patient) => patient.mrn)
      .filter((value) => value !== undefined) as string[];
  };

  const allPatientOptions = useMemo(
    () =>
      allPatients
        ? allPatients.map((patient) => ({
            description: patient.firstName + ' ' + patient.lastName,
            name: patient.id,
          }))
        : [],
    [allPatients],
  );

  const addTherapistToPatient = async (patient: Patient, therapistId: number) => {
    const {
      diagnosis,
      ethnicity,
      fullImageUrl: _f,
      gender,
      guardian: _g,
      race,
      sex,
      status: _s,
      therapists,
      ...data
    } = patient;
    const payload: PatientPayload = {
      ...data,
      diagnosis: diagnosis.map((dia) => dia.name),
      therapistIds: therapists.map(({ id }) => id).concat([therapistId]),
      ethnicity: ethnicity.name,
      gender: gender.name,
      race: race.name,
      sex: sex.name,
    };
    return await updatePatient(payload);
  };

  return {
    activePatients,
    addTherapistToPatient,
    allPatients: allPatients?.sort((patientA, patientB) => {
      const nameA = patientA.lastName.toUpperCase();
      const nameB = patientB.lastName.toUpperCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    }),
    allPatientOptions,
    deletePatient,
    getPatientMRNsByHospital,
    therapistHospitalPatientMRNs,
    isActiveFetching,
    isLoading,
    searchPatients,
    therapistPatients,
    therapistPatientsOptions,
  };
};

export default usePatients;
